import { POST_PATHS, buildPostActionPath, buildPostCommunityFeedPath, buildPostPath, readPostFeedResponse, readPostReactionResponse, readPostResponse, readPostWriteRequest, readPostCommentRequest, readPostFeedRequest, readPostReactionRequest } from '@liquid-spirit/api/domains/posts';
import { readUploadVideoResponse, readUploadImageResponse, readUploadS3VideoUrlResponse, UPLOAD_PATHS } from '@liquid-spirit/api/domains/uploads';
import { API_BASE_URL } from '../config/apiBase';
import apiClient from './apiClient';
import { cacheFetch } from './cacheFetch.js';
import { resolveStoredAuthToken } from '../utils/storage';
import { uploadWithProgress } from '../utils/uploadWithProgress';
import maintainerMetrics from './maintainerMetrics';
import { invalidatePostCaches } from './frontendCacheInvalidation';

const readToken = () => resolveStoredAuthToken();

const isAbortError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError' || error.code === 'ABORT_ERR') return true;
  const message = String(error.message || '').toLowerCase();
  return message.includes('aborted');
};

const startTrackedRequest = (path, method = 'GET', source = 'PostService') =>
  maintainerMetrics.recordApiRequestMetricStart({
    path,
    method,
    source,
  });

const finishTrackedRequest = (requestMetric, {
  status = null,
  payload = null,
  cacheStatus = 'network',
  error = null,
} = {}) =>
  maintainerMetrics.recordApiRequestMetricEnd(requestMetric, {
    status,
    payload,
    cacheStatus,
    error,
  });

export const NEWS_FEED_PAGE_SIZE = 9;

export const fetchCommunityFeedPage = async (communityId, {
  cursor = null,
  limit = NEWS_FEED_PAGE_SIZE,
  signal,
  token = readToken(),
} = {}) => {
  const { payload } = await apiClient.request(buildPostCommunityFeedPath(communityId), {
    method: 'GET',
    token,
    signal,
    params: readPostFeedRequest({ pagination: 'cursor', limit, ...(cursor ? { cursor } : {}) }),
    contract: 'raw',
  });
  const { data, pagination } = payload || {};
  if (!Array.isArray(data) || typeof pagination?.hasMore !== 'boolean'
    || (pagination.hasMore && (!data.length || typeof pagination.nextCursor !== 'string'
      || !pagination.nextCursor || pagination.nextCursor === cursor))) {
    throw new Error('The news feed returned an invalid page. Please try again.');
  }
  readPostFeedResponse(payload, { requirePagination: true });
  return {
    posts: data,
    hasMore: pagination.hasMore,
    nextCursor: pagination.hasMore ? pagination.nextCursor : null,
  };
};

export const fetchCommunityFeed = async (userCommunityId, options = {}) => {
  if (!userCommunityId) {
    return [];
  }

  const token = readToken();
  if (!token) {
    return [];
  }

  try {
    const { params, cacheKey, ...cacheOptions } = options;
    if (params !== undefined) readPostFeedRequest(params);
    const query = params && Object.keys(params).length
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    const feedPath = `${buildPostCommunityFeedPath(userCommunityId)}${query}`;
    const data = await cacheFetch(feedPath, {
      key: cacheKey || `news:feed:${userCommunityId}${query}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      token,
      staleMs: 60_000,
      // Do not persist here to avoid diverging from NewsContext persistence
      ...cacheOptions,
      validateResponse: readPostFeedResponse,
      allowStaleOnError: false,
    });
    // API returns { data: [...] }
    return readPostFeedResponse(data).data;
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    const message = (error?.message || '').toLowerCase();
    if (message.includes('authorization denied') || message.includes('unauthorized')) {
      return [];
    }
    console.error('Error fetching community feed:', error);
    throw error;
  }
};
  
  export const fetchRecentCommunityPosts = async (userCommunityId) => {
    const path = buildPostCommunityFeedPath(userCommunityId);
    const requestMetric = startTrackedRequest(path, 'GET');
    try {
      const result = await apiClient.request(path, {
        method: 'GET',
        contract: 'raw',
        skipMetrics: true,
      });

      finishTrackedRequest(requestMetric, {
        status: result.status,
        payload: result.payload,
      });
      return readPostFeedResponse(result.payload).data.slice(0, 5);
    } catch (error) {
      finishTrackedRequest(requestMetric, {
        status: error?.status ?? null,
        error,
      });
      console.error('Error fetching recent posts:', error);
      throw error;
    }
  };

export const fetchPostDetails = async (postId, options = {}) => {
  const {
    useCache = true,
    forceRefresh = false,
    forceFresh = false,
    staleMs: forcedStaleMs,
    revalidate: forcedRevalidate,
    signal,
    key,
    ...rest
  } = options;
  const shouldForceRefresh = forceFresh || forceRefresh;
  const token = readToken();
  const path = buildPostPath(postId);
  try {
    const data = await cacheFetch(path, {
      key: key || `posts:item:${postId}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      token,
      staleMs: shouldForceRefresh
        ? 0
        : (forcedStaleMs ?? 5 * 60_000),
      revalidate: shouldForceRefresh
        ? false
        : (forcedRevalidate ?? true),
      useCache: shouldForceRefresh ? false : useCache,
      signal,
      ...rest,
      validateResponse: readPostResponse,
      allowStaleOnError: false,
    });
    return readPostResponse(data).data;
  } catch (error) {
      console.error('Error fetching post details:', error);
      const wrapped = new Error(`Fetch post details error: ${error.message}`);
      wrapped.status = error?.status || null;
      wrapped.code = error?.code;
      wrapped.data = error?.data;
      throw wrapped;
    }
  };

  export const likePost = async (postId) => {
    const path = buildPostActionPath(postId, 'like');
    const requestMetric = startTrackedRequest(path, 'POST');
    try {
      const result = await apiClient.request(path, {
        method: 'POST',
        contract: 'raw',
        skipMetrics: true,
      });

      finishTrackedRequest(requestMetric, {
        status: result.status,
        payload: result.payload,
      });
      readPostResponse(result.payload);
      return result.payload?.data; // Return updated post data or like count
    } catch (error) {
      finishTrackedRequest(requestMetric, {
        status: error?.status ?? null,
        error,
      });
      console.error('Error liking post:', error);
      throw error;
    }
  };

  export const commentOnPost = async (postId, commentText, {parentCommentId} = {}) => {
    const path = buildPostActionPath(postId, 'comment');
    const requestMetric = startTrackedRequest(path, 'POST');
    try {
      const result = await apiClient.request(path, {
        method: 'POST',
        body: readPostCommentRequest({ comment: commentText, ...(parentCommentId !== undefined ? {parentCommentId} : {}) }),
        contract: 'raw',
        skipMetrics: true,
      });

      finishTrackedRequest(requestMetric, {
        status: result.status,
        payload: result.payload,
      });
      readPostResponse(result.payload);
      return result.payload?.data; // Return updated post data or comment data
    } catch (error) {
      finishTrackedRequest(requestMetric, {
        status: error?.status ?? null,
        error,
      });
      console.error('Error commenting on post:', error);
      throw error;
    }
  };

  export const createPost = async ({ content, mediaUrls, thumbnailUrls, user, userCommunityId }) => {
    const requestMetric = startTrackedRequest(POST_PATHS.create, 'POST');
    
    try {
      const result = await apiClient.request(POST_PATHS.create, {
        method: 'POST',
        body: readPostWriteRequest({
          content,
          media: mediaUrls || [],
          mediaThumbnails: thumbnailUrls || [],
          author: user.id,
          community: userCommunityId,
        }),
        contract: 'raw',
        skipMetrics: true,
      });

      finishTrackedRequest(requestMetric, {
        status: result.status,
        payload: result.payload,
      });
      readPostResponse(result.payload);
      invalidatePostCaches({ communityId: userCommunityId });
      return result.payload?.data;
    } catch (error) {
      finishTrackedRequest(requestMetric, {
        status: error?.status ?? null,
        error,
      });
      console.error('Error creating post:', error);
      throw error;
    }
  };

  export const uploadImageWithThumbnail = async (file, onProgress) => {
    const token = readToken();
  
    try {
      const formData = new FormData();
      formData.append('file', file);
  
      const { data } = await uploadWithProgress(`${API_BASE_URL}${UPLOAD_PATHS.uploadImage}`, {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        onProgress: (percentCompleted) => {
          onProgress(percentCompleted >= 95 ? 95 : percentCompleted);
        },
      });
      readUploadImageResponse(data);
      return {
        imageUrl: data.originalUrl,
        thumbnailUrl: data.thumbnailUrl,
      };
    } catch (err) {
      console.error('Image upload error:', err);
      throw new Error('Failed to upload image.');
    }
  };

  export const getS3VideoUploadUrl = async (fileName, fileType) => {
    const requestMetric = startTrackedRequest(UPLOAD_PATHS.s3VideoUrl, 'GET');
    try {
      const result = await apiClient.request(UPLOAD_PATHS.s3VideoUrl, {
        method: 'GET',
        params: { fileName, fileType },
        contract: 'raw',
        skipMetrics: true,
      });
      finishTrackedRequest(requestMetric, {
        status: result.status,
        payload: result.payload,
      });
      return readUploadS3VideoUrlResponse(result.payload);
    } catch (error) {
      finishTrackedRequest(requestMetric, {
        status: error?.status ?? null,
        error,
      });
      throw new Error('Failed to get upload URL.');
    }
  };

  export const uploadVideoWithThumbnail = async (file, onProgress) => {
    const token = readToken();
  
    try {
      const { url } = await getS3VideoUploadUrl(file.name, file.type);
      if (!url) throw new Error('Failed to get signed URL');
  
      // === Upload to S3
      await uploadWithProgress(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
        onProgress: (percent) => {
          onProgress(percent >= 95 ? 95 : percent);
        },
      });
  
      const videoUrl = url.split('?')[0];
  
      const { thumbnailUrl } = readUploadVideoResponse(await apiClient.request(UPLOAD_PATHS.uploadVideo, {
        method: 'POST',
        token,
        body: { videoUrl },
        contract: 'payload',
      }));
      return {
        videoUrl,
        thumbnailUrl,
      };
    } catch (err) {
      console.error('Video upload error:', err);
      throw new Error('Failed to upload video or generate thumbnail.');
    }
  };

  export const uploadVideoToS3 = async (file, url, onProgress) => {
    await uploadWithProgress(url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
      onProgress,
    });
    return url.split('?')[0];
  };
  

export const setCommentReaction = async (postId, commentId, emoji) => {
  readPostReactionRequest(postId, emoji, commentId);
  const {payload} = await apiClient.request(`/api/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/reaction`, {
    method: 'PUT', token: readToken(), body: {emoji}, contract: 'raw',
  });
  readPostReactionResponse(payload, 'comment', {id: commentId});
  invalidatePostCaches({postId});
  return payload?.data || payload;
};

export const setPostReaction = async (postId, emoji) => {
  readPostReactionRequest(postId, emoji);
  const {payload} = await apiClient.request(`/api/posts/${encodeURIComponent(postId)}/reaction`, {
    method: 'PUT', token: readToken(), body: {emoji}, contract: 'raw',
  });
  readPostReactionResponse(payload, 'post', {id: postId});
  invalidatePostCaches({postId});
  return payload?.data || payload;
};
